import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import getGqlClient from '@spotahome/ui-library/src/tenant/clients/graphql/marketplace';

import { getAvailabilitiesCityStatistics } from '@spotahome/marketplace-common/src/graphql/queries/searchStatistics';
import { clientErrorTracker } from '@spotahome/soyuz/client';
import {
  formatMonthAndYearAbbrvDate,
  formatDate
} from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import { capitalizeFirstLetter } from '@spotahome/ui-library/src/tenant/components/utils/stringUtils';

import { getQueryPropertyType } from '@spotahome/marketplace-common/src/utils/seoCharts';

const DECIMAL_DIGITS = 1;

const defaultFn = () => {};

const normalizeData = data => {
  return data?.map(item => {
    const fullDate = new Date();
    fullDate.setMonth(item.month - 1);
    fullDate.setFullYear(item.year);
    return {
      ...item,
      label: capitalizeFirstLetter(formatMonthAndYearAbbrvDate(fullDate)),
      monthName: formatDate(fullDate, 'MMMM')
    };
  });
};

const AvailabilitiesStatisticsContext = createContext({
  availabilitiesStatistics: [],
  loading: false,
  setPropertyType: defaultFn,
  highestMonthStats: {},
  lowestMonthStats: {},
  average: null
});

export const AvailabilitiesStatisticsProvider = ({
  children,
  cityId,
  propertyType: initialPropertyType,
  availabilitiesStatistics: initialAvailabilitiesStatistics = []
}) => {
  const allStats = normalizeData(initialAvailabilitiesStatistics);

  const [loading, setLoading] = useState(false);
  const [propertyType, setInternalPropertyType] = useState(initialPropertyType);
  const [availabilitiesStatistics, setAvailabilitiesStatistics] =
    useState(allStats);

  const requestAvailabitiesStatistics = async () => {
    setLoading(true);
    try {
      const response = await getAvailabilitiesCityStatistics(getGqlClient())({
        cityId,
        propertyType: getQueryPropertyType(propertyType)
      });

      setAvailabilitiesStatistics(
        normalizeData(response?.data?.cityAvailabilitiesStatistics ?? [])
      );
    } catch (error) {
      setAvailabilitiesStatistics([]);
      clientErrorTracker.error({
        error,
        source: 'AvailabilitiesStatisticsProvider-requestAvailabitiesStatistics'
      });
    }
    setLoading(false);
  };

  const getStats = () =>
    availabilitiesStatistics.length > 0 ? availabilitiesStatistics : allStats;

  const getAverage = () => {
    const stats = getStats();
    const total = stats.reduce(
      (acc, item) => acc + parseFloat(item.percentage),
      0
    );
    return parseFloat(total / stats.length).toFixed(DECIMAL_DIGITS);
  };

  const getHighestMonthStats = () =>
    getStats().reduce(
      (prev, current) =>
        prev.percentage > current.percentage ? prev : current,
      { percentage: -Infinity }
    );

  const getLowestMonthStats = () =>
    getStats().reduce(
      (prev, current) =>
        prev.percentage < current.percentage ? prev : current,
      { percentage: Infinity }
    );

  useEffect(() => {
    requestAvailabitiesStatistics();
  }, [propertyType]);

  const setPropertyType = newPropertyType => {
    if (newPropertyType !== propertyType) {
      setInternalPropertyType(newPropertyType);
    }
  };

  return (
    <AvailabilitiesStatisticsContext.Provider
      value={{
        cityId,
        propertyType,
        initialPropertyType,
        availabilitiesStatistics,
        setPropertyType,
        highestMonthStats: getHighestMonthStats(),
        lowestMonthStats: getLowestMonthStats(),
        average: getAverage(),
        loading
      }}
    >
      {children}
    </AvailabilitiesStatisticsContext.Provider>
  );
};

AvailabilitiesStatisticsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  cityId: PropTypes.string.isRequired,
  propertyType: PropTypes.string.isRequired,
  availabilitiesStatistics: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string,
      year: PropTypes.string,
      percentage: PropTypes.number,
      delta: PropTypes.number
    })
  )
};

export const useAvailabilitiesStatisticsContext = () =>
  useContext(AvailabilitiesStatisticsContext);

export default {
  AvailabilitiesStatisticsProvider,
  useAvailabilitiesStatisticsContext
};
